import { teamApi } from "@/api/team-api";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useWorkspaces } from "@/store/workspace-store";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getErrorMessage } from "@/lib/api-error";
import { Trash } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

interface NewMember {
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  dealsReviewed: number;
  createdOn: string;
}

export default function TeamComponent() {
  const { activeWorkspaceId } = useWorkspaces();
  const queryClient = useQueryClient();

  const { data: teamData, isLoading } = useQuery({
    queryKey: ["team", activeWorkspaceId],
    queryFn: () => teamApi.getTeamMembers(activeWorkspaceId || ""),
    enabled: !!activeWorkspaceId,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [newMember, setNewMember] = useState<NewMember>({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    dealsReviewed: 0,
    createdOn: new Date().toISOString(),
  });

  const validateForm = () => {
    if (!newMember.firstName.trim()) return "First name is required";
    if (!newMember.lastName.trim()) return "Last name is required";
    if (!newMember.email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newMember.email))
      return "Invalid email format";
    if (!newMember.role) return "Role is required";
    return null;
  };

  const createJudgeMutation = useMutation({
    mutationFn: async (newMember: NewMember) => {
      const judgeResponse = await teamApi.createJudge({
        first_name: newMember.firstName,
        last_name: newMember.lastName,
        email: newMember.email,
        sub_vertical: newMember.role,
      });

      if (judgeResponse.status === "error") {
        throw new Error(judgeResponse.message || "Failed to add team member");
      }

      if (!judgeResponse.data) {
        throw new Error("Failed to add team member");
      }

      // Create affiliation for user using ID
      await teamApi.createAffiliation({
        user: judgeResponse.data._id,
        opportunity: activeWorkspaceId || "",
        status: "approved",
        role: newMember.role,
      });

      return judgeResponse;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", activeWorkspaceId] });
      setDialogOpen(false);
      setNewMember({
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        dealsReviewed: 0,
        createdOn: new Date().toISOString(),
      });
      toast.success("Team member added successfully");
    },
    onError: (error: Error) => {
      toast.error(getErrorMessage(error, "Failed to add team member"));
    },
  });

  const deleteJudgeMutation = useMutation({
    mutationFn: async (judgeId: string) => {
      return teamApi.deleteJudge(judgeId, activeWorkspaceId || "");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", activeWorkspaceId] });
      toast.success("Team member removed successfully");
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, "Failed to remove team member"));
    },
  });

  const handleAdd = () => {
    const error = validateForm();
    if (error) {
      toast.error(error);
      return;
    }

    createJudgeMutation.mutate(newMember);
  };

  const handleCancel = () => {
    setDialogOpen(false);
    setNewMember({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      dealsReviewed: 0,
      createdOn: new Date().toISOString(),
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full px-2 sm:px-4 pt-4 sm:pt-6 lg:p-6">
        <div className="flex justify-between items-center mb-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-9 w-28" />
        </div>

        {/* Desktop table skeleton */}
        <div className="hidden md:block rounded-lg border overflow-hidden">
          <div className="bg-muted/50 border-b px-4 py-2">
            <div className="grid grid-cols-8 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-20" />
              ))}
            </div>
          </div>
          {Array.from({ length: 5 }).map((_, rowIdx) => (
            <div key={rowIdx} className="border-b last:border-b-0 px-4 py-3">
              <div className="grid grid-cols-8 gap-4 items-center">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-8" />
              </div>
            </div>
          ))}
        </div>

        {/* Mobile cards skeleton */}
        <div className="grid grid-cols-1 gap-4 md:hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-lg border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <Skeleton className="h-5 w-40" />
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Map backend type values to display labels
  const getDisplayType = (type: string | undefined): string => {
    if (!type) return "-";
    if (type === "company manager") return "admin";
    return type;
  };

  const displayMembers =
    teamData?.opp_judges?.map((judge) => {
      const primaryEmail = judge.clerk_primary_email || judge.email;
      const additionalEmails = (judge.clerk_emails || []).filter(
        (e) => e !== primaryEmail
      );
      return {
        id: judge._id,
        firstName: judge.first_name,
        lastName: judge.last_name,
        email: primaryEmail,
        additionalEmails,
        role: judge.role || judge.sub_vertical || "-",
        type: getDisplayType(judge.type),
        dealsReviewed: Array.isArray(judge.reviews) ? judge.reviews.length : 0,
        createdOn: judge.created_on,
      };
    }) || [];

  return (
    <div className="px-2 sm:px-4 pt-4 sm:pt-6 lg:p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Team Members</h1>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add Member</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Team Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={newMember.firstName}
                  onChange={(e) =>
                    setNewMember({ ...newMember, firstName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={newMember.lastName}
                  onChange={(e) =>
                    setNewMember({ ...newMember, lastName: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMember.email}
                  onChange={(e) =>
                    setNewMember({ ...newMember, email: e.target.value })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newMember.role}
                  onValueChange={(value) =>
                    setNewMember({ ...newMember, role: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Managing Director">
                      Managing Director
                    </SelectItem>
                    <SelectItem value="Limited Partner">
                      Limited Partner
                    </SelectItem>
                    <SelectItem value="Executive">Executive</SelectItem>
                    <SelectItem value="Partner">Partner</SelectItem>
                    <SelectItem value="Program Director">
                      Program Director
                    </SelectItem>
                    <SelectItem value="Associate">Associate</SelectItem>
                    <SelectItem value="Analyst">Analyst</SelectItem>
                    <SelectItem value="Advisor">Advisor</SelectItem>
                    <SelectItem value="Mentor">Mentor</SelectItem>
                    <SelectItem value="Reviewer">Reviewer</SelectItem>
                    <SelectItem value="Angel">Angel</SelectItem>
                    <SelectItem value="Selection Committee">
                      Selection Committee
                    </SelectItem>
                    <SelectItem value="EIR">EIR</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={handleAdd}
                  className="flex-1"
                  disabled={createJudgeMutation.isPending}
                >
                  {createJudgeMutation.isPending ? "Adding..." : "Add"}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleCancel}
                  className="flex-1"
                  disabled={createJudgeMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Desktop view */}
      <div className="hidden md:block rounded-lg border">
        <table className="w-full table-fixed">
          {displayMembers.length > 0 && (
            <thead>
              <tr className="border-b bg-muted/50 text-sm text-left">
                <th className="p-2 w-[70px]"></th>
                <th className="p-2 hidden lg:table-cell font-medium">Name</th>
                <th className="p-2 font-medium">Email</th>
                <th className="p-2 font-medium w-[100px]">Role</th>
                <th className="p-2 font-medium w-[100px]">Access Type</th>
                <th className="p-2 font-medium w-[90px]">Deals Reviewed</th>
                <th className="p-2 font-medium w-[100px]">Added On</th>
                <th className="p-2 font-medium w-[50px]"></th>
              </tr>
            </thead>
          )}
          <tbody className="text-sm">
            {displayMembers.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="p-4 text-center text-muted-foreground"
                >
                  No team members found
                </td>
              </tr>
            ) : (
              displayMembers.map((member, index) => (
                <tr
                  key={member.email}
                  className={
                    index !== displayMembers.length - 1 ? "border-b" : ""
                  }
                >
                  <td className="p-2 pl-5">
                    <Avatar>
                      <AvatarFallback
                        style={{ backgroundColor: "#C49733", color: "black" }}
                      >
                        {`${member.firstName[0]}${member.lastName[0]}`}
                      </AvatarFallback>
                    </Avatar>
                  </td>
                  <td className="p-1 hidden lg:table-cell truncate">{`${member.firstName} ${member.lastName}`}</td>
                  <td className="p-1">
                    <div className="truncate">{member.email}</div>
                    {member.additionalEmails.map((e) => (
                      <div key={e} className="truncate text-xs text-muted-foreground">{e}</div>
                    ))}
                  </td>
                  <td className="p-1">{member.role || "-"}</td>
                  <td className="p-1 capitalize">{member.type}</td>
                  <td className="p-1">{member.dealsReviewed}</td>
                  <td className="p-1">
                    {new Date(member.createdOn).toLocaleDateString()}
                  </td>
                  <td className="p-1 text-left">
                    <Button
                      size={"icon"}
                      variant="ghost"
                      onClick={() => deleteJudgeMutation.mutate(member.id)}
                    >
                      <Trash className="text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile view */}
      <div className="grid grid-cols-1 gap-4 md:hidden">
        {displayMembers.length === 0 ? (
          <Card>
            <CardContent className="p-4 text-center text-muted-foreground">
              No team members found
            </CardContent>
          </Card>
        ) : (
          displayMembers.map((member) => (
            <Card key={member.email}>
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarFallback
                      style={{ backgroundColor: "#C49733", color: "black" }}
                    >
                      {`${member.firstName[0]}${member.lastName[0]}`}
                    </AvatarFallback>
                  </Avatar>
                  <CardTitle>{`${member.firstName} ${member.lastName}`}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <span className="font-medium">Email:</span> {member.email}
                    {member.additionalEmails.map((e) => (
                      <div key={e} className="text-xs text-muted-foreground">{e}</div>
                    ))}
                  </div>
                  <div>
                    <span className="font-medium">Role:</span> {member.role}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {member.type}
                  </div>
                  <div>
                    <span className="font-medium">Deals Reviewed:</span>{" "}
                    {member.dealsReviewed}
                  </div>
                  <div>
                    <span className="font-medium">Added On:</span>{" "}
                    {new Date(member.createdOn).toLocaleDateString()}
                  </div>
                  <Button
                    variant="destructive"
                    className="w-full mt-2"
                    onClick={() => deleteJudgeMutation.mutate(member.id)}
                  >
                    <Trash />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
